import React, { useMemo, useState } from "react";

type Priority = "low" | "medium" | "high" | "urgent";
type Status = "open" | "in_progress" | "waiting" | "closed";

interface Ticket {
    id: string;
    title: string;
    requester: string;
    channel: string;
    createdAt: string;
    lastActivity: string;
    priority: Priority;
    status: Status;
    tags: string[];
    unread: boolean;
}

interface ModuleCard {
    id: string;
    name: string;
    status: "active" | "paused";
    description: string;
    scope: string;
}

interface CommandStat {
    name: string;
    usage: number;
    latency: string;
    success: number;
}

interface EventLog {
    id: string;
    type: "info" | "warn" | "error";
    detail: string;
    time: string;
}

const priorityLabel: Record<Priority, string> = {
    low: "Niski",
    medium: "Sredni",
    high: "Wysoki",
    urgent: "Pilny",
};

const statusLabel: Record<Status, string> = {
    open: "Otwarte",
    in_progress: "W trakcie",
    waiting: "Oczekuje",
    closed: "Zamkniete",
};

const priorityColor: Record<Priority, string> = {
    low: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
    medium: "bg-sky-500/10 text-sky-200 border-sky-500/40",
    high: "bg-amber-500/10 text-amber-200 border-amber-500/40",
    urgent: "bg-rose-500/10 text-rose-200 border-rose-500/40",
};

const statusDotColor: Record<Status, string> = {
    open: "bg-emerald-400",
    in_progress: "bg-sky-400",
    waiting: "bg-amber-400",
    closed: "bg-slate-500",
};

const mockTickets: Ticket[] = [
    {
        id: "TCK-2042",
        title: "Ticket close zwraca blad perms na serwerze EU-3",
        requester: "Neo#1337",
        channel: "#support",
        createdAt: "2025-11-19T13:42:00Z",
        lastActivity: "2025-11-20T10:15:00Z",
        priority: "high",
        status: "in_progress",
        tags: ["bug", "production"],
        unread: true,
    },
    {
        id: "TCK-2041",
        title: "Prosba o wlaczenie auto-taggingu dla /help",
        requester: "Luna#4920",
        channel: "#ideas",
        createdAt: "2025-11-18T09:12:00Z",
        lastActivity: "2025-11-19T20:05:00Z",
        priority: "medium",
        status: "open",
        tags: ["feature", "ux"],
        unread: true,
    },
    {
        id: "TCK-2039",
        title: "Bot nie odpowiada na /help",
        requester: "Raven#9001",
        channel: "#support",
        createdAt: "2025-11-17T18:22:00Z",
        lastActivity: "2025-11-18T07:51:00Z",
        priority: "urgent",
        status: "waiting",
        tags: ["bug", "api"],
        unread: false,
    },
];

const moduleCards: ModuleCard[] = [
    {
        id: "mod-1",
        name: "Automatyczne tagi",
        status: "active",
        description: "Reguly dla slow kluczowych i komend.",
        scope: "3 serwery / 12 kanalow",
    },
    {
        id: "mod-2",
        name: "Kolejka live",
        status: "active",
        description: "Eskalacje i priorytety dla krytycznych ticketow.",
        scope: "Wszystkie serwery",
    },
    {
        id: "mod-3",
        name: "Auto odpowiedzi",
        status: "paused",
        description: "Snippety dla powtarzalnych pytan.",
        scope: "Support / helpdesk",
    },
];

const commandStats: CommandStat[] = [
    { name: "/ticket create", usage: 182, latency: "220ms", success: 99 },
    { name: "/ticket close", usage: 96, latency: "310ms", success: 92 },
    { name: "/help", usage: 321, latency: "150ms", success: 100 },
];

const eventLog: EventLog[] = [
    { id: "ev-1", type: "info", detail: "Webhook: TICKET_CREATED na #support", time: "09:21" },
    { id: "ev-2", type: "warn", detail: "Regula: priorytet podniesiony na Wysoki", time: "09:31" },
    { id: "ev-3", type: "info", detail: "Mod Raven przejal ticket", time: "09:32" },
    { id: "ev-4", type: "error", detail: "API: rate limit przy /ticket close", time: "09:35" },
];

const Test: React.FC = () => {
    const [tickets] = useState<Ticket[]>(mockTickets);
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(mockTickets[0]?.id ?? null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
    const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");

    const selectedTicket = useMemo(() => tickets.find((t) => t.id === selectedTicketId) ?? null, [tickets, selectedTicketId]);

    const filteredTickets = useMemo(() => {
        return tickets.filter((t) => {
            const matchesSearch =
                t.title.toLowerCase().includes(search.toLowerCase()) ||
                t.id.toLowerCase().includes(search.toLowerCase()) ||
                t.requester.toLowerCase().includes(search.toLowerCase());
            const matchesStatus = statusFilter === "all" || t.status === statusFilter;
            const matchesPriority = priorityFilter === "all" || t.priority === priorityFilter;
            return matchesSearch && matchesStatus && matchesPriority;
        });
    }, [tickets, search, statusFilter, priorityFilter]);

    const stats = useMemo(() => {
        const total = tickets.length;
        const open = tickets.filter((t) => t.status === "open").length;
        const inProgress = tickets.filter((t) => t.status === "in_progress").length;
        const unread = tickets.filter((t) => t.unread).length;
        return { total, open, inProgress, unread };
    }, [tickets]);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100">
            <div className="flex min-h-screen">
                <Sidebar />

                <main className="flex-1 flex flex-col">
                    <Header />

                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                        <StatsRow stats={stats} />

                        <TopControls />

                        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <TicketList
                                tickets={filteredTickets}
                                selectedTicketId={selectedTicketId}
                                onSelect={setSelectedTicketId}
                                setSearch={setSearch}
                                setStatusFilter={setStatusFilter}
                                setPriorityFilter={setPriorityFilter}
                                search={search}
                                statusFilter={statusFilter}
                                priorityFilter={priorityFilter}
                            />

                            <div className="rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40">
                                {selectedTicket ? (
                                    <TicketDetailPanel ticket={selectedTicket} />
                                ) : (
                                    <div className="h-full flex items-center justify-center text-sm text-slate-500 p-6">
                                        Wybierz ticket aby zobaczyc szczegoly.
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <Modules />
                            <CommandUsage />
                        </section>

                        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <Conversation requester={selectedTicket?.requester ?? "uzytkownik"} id={selectedTicket?.id ?? "TCK-XXXX"} />
                            <EventStream />
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

const Sidebar: React.FC = () => (
    <aside className="hidden lg:flex w-64 flex-col border-r border-slate-800/80 bg-gradient-to-b from-slate-950 via-slate-950/90 to-slate-900/60 backdrop-blur-xl">
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-sky-500 via-indigo-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-sky-500/30 font-semibold text-slate-900">
                MB
            </div>
            <div>
                <div className="font-semibold tracking-tight">Myster Bot</div>
                <div className="text-xs text-slate-400">Panel moderatora</div>
            </div>
        </div>

        <nav className="px-3 py-4 space-y-1 text-sm">
            <SidebarItem active label="Dashboard" />
            <SidebarItem label="Tickets" />
            <SidebarItem label="Automatyzacje" />
            <SidebarItem label="Komendy" />
            <SidebarItem label="Logi bota" />
            <SidebarItem label="Ustawienia" />
        </nav>

        <div className="mt-auto p-4 space-y-3 text-xs">
            <div className="rounded-2xl border border-sky-500/30 bg-slate-900/60 p-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-200 font-medium">Status bota</span>
                    <span className="flex items-center gap-1 text-emerald-300">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                        Online
                    </span>
                </div>
                <p className="text-slate-400 leading-relaxed">
                    3 serwery, 128 aktywnych ticketow. Uptime 99.98%.
                </p>
            </div>

            <button className="w-full rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 text-sm font-medium py-2.5 transition-colors">
                Nowy ticket testowy
            </button>
        </div>
    </aside>
);

const Header: React.FC = () => (
    <header className="border-b border-slate-800/70 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
            <div>
                <h1 className="text-base sm:text-lg font-semibold tracking-tight">Dashboard bota Discord</h1>
                <p className="text-xs sm:text-sm text-slate-400">Podglad stanu serwerow, automatyzacji i ticket center.</p>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
                <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/60 px-3 py-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-slate-300">Live stream eventow</span>
                </div>
                <button className="rounded-full border border-slate-800/80 bg-slate-900/60 px-2.5 py-1.5 hover:border-sky-500/60 hover:text-sky-300 transition-colors text-xs">
                    Motyw
                </button>
            </div>
        </div>
    </header>
);

const StatsRow: React.FC<{ stats: { total: number; open: number; inProgress: number; unread: number } }> = ({
    stats,
}) => (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatsCard label="Aktywne tickety" value={stats.total} trend="+12%" tone="primary" />
        <StatsCard label="Otwarte" value={stats.open} trend="+3" tone="neutral" />
        <StatsCard label="W trakcie" value={stats.inProgress} trend="-1" tone="success" />
        <StatsCard label="Nieprzeczytane" value={stats.unread} trend="+2" tone="alert" />
    </section>
);

const TopControls: React.FC = () => (
    <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="col-span-2 rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-sm font-semibold">Bot live</h2>
                    <p className="text-xs text-slate-400">Szybkie sterowanie stanem i monitorowanie uptime.</p>
                </div>
                <span className="text-xs text-emerald-300 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    Uptime 99.98%
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ControlCard title="Tryb produkcyjny" subtitle="Webhooki i komendy" state="ON" tone="success" />
                <ControlCard title="Tryb serwisowy" subtitle="Blokada komend" state="OFF" tone="neutral" />
                <ControlCard title="Alerty" subtitle="DM do modow" state="ON" tone="alert" />
            </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-2">
            <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">Szybkie akcje</h2>
                <span className="text-[11px] text-slate-500">Bot</span>
            </div>
            <div className="space-y-2 text-xs">
                <QuickAction label="Restart bota" detail="Ping na wszystkich shardach" />
                <QuickAction label="Wyczysc cache komend" detail="Force refresh global commands" />
                <QuickAction label="Dodaj note do ticketu" detail="Tag: escalated" />
            </div>
        </div>
    </section>
);

interface TicketListProps {
    tickets: Ticket[];
    selectedTicketId: string | null;
    onSelect: (id: string) => void;
    setSearch: (val: string) => void;
    setStatusFilter: (val: Status | "all") => void;
    setPriorityFilter: (val: Priority | "all") => void;
    search: string;
    statusFilter: Status | "all";
    priorityFilter: Priority | "all";
}

const TicketList: React.FC<TicketListProps> = ({
    tickets,
    selectedTicketId,
    onSelect,
    setSearch,
    setStatusFilter,
    setPriorityFilter,
    search,
    statusFilter,
    priorityFilter,
}) => (
    <div className="col-span-2 rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40">
        <div className="px-4 py-3 border-b border-slate-800/70 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div>
                <h2 className="text-sm font-semibold">Ticket center</h2>
                <p className="text-xs text-slate-400">Live kolejka z filtrem statusu i priorytetu.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1 min-w-[200px]">
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Szukaj po ID, tytule lub uzytkowniku"
                        className="w-full rounded-xl bg-slate-900/70 border border-slate-800 px-8 py-2 text-xs sm:text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/70 focus:border-sky-500/70"
                    />
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-500 text-sm">/</span>
                </div>
                <div className="flex gap-2">
                    <SelectChip
                        label="Status"
                        value={statusFilter}
                        options={[
                            { value: "all", label: "Wszystkie" },
                            { value: "open", label: statusLabel.open },
                            { value: "in_progress", label: statusLabel.in_progress },
                            { value: "waiting", label: statusLabel.waiting },
                            { value: "closed", label: statusLabel.closed },
                        ]}
                        onChange={(v) => setStatusFilter(v as Status | "all")}
                    />
                    <SelectChip
                        label="Priorytet"
                        value={priorityFilter}
                        options={[
                            { value: "all", label: "Wszystkie" },
                            { value: "low", label: priorityLabel.low },
                            { value: "medium", label: priorityLabel.medium },
                            { value: "high", label: priorityLabel.high },
                            { value: "urgent", label: priorityLabel.urgent },
                        ]}
                        onChange={(v) => setPriorityFilter(v as Priority | "all")}
                    />
                </div>
            </div>
        </div>

        <div className="h-[420px] overflow-auto">
            <ul className="divide-y divide-slate-800/80">
                {tickets.map((ticket) => (
                    <li
                        key={ticket.id}
                        className={`cursor-pointer border-l-2 transition-all ${ticket.id === selectedTicketId
                                ? "border-sky-500/80 bg-slate-900/70"
                                : "border-transparent hover:border-slate-700 hover:bg-slate-900/40"
                            }`}
                        onClick={() => onSelect(ticket.id)}
                    >
                        <TicketRow ticket={ticket} />
                    </li>
                ))}

                {tickets.length === 0 && (
                    <li className="px-4 sm:px-6 py-8 text-center text-sm text-slate-500">
                        Brak ticketow spelniajacych kryteria. Zmien filtry lub wyszukiwanie.
                    </li>
                )}
            </ul>
        </div>
    </div>
);

const TicketRow: React.FC<{ ticket: Ticket }> = ({ ticket }) => (
    <div className="px-4 sm:px-6 py-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-slate-500">{ticket.id}</span>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${priorityColor[ticket.priority]}`}>
                    {priorityLabel[ticket.priority]}
                </span>
                {ticket.unread && (
                    <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 text-[10px]">
                        nowe
                    </span>
                )}
            </div>
            <span className="text-[11px] text-slate-500">{formatTime(ticket.lastActivity)}</span>
        </div>

        <div>
            <p className="text-sm font-medium text-slate-100 line-clamp-1">{ticket.title}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                <span className="flex items-center gap-1">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-slate-800/80 text-[10px]">U</span>
                    {ticket.requester}
                </span>
                <span className="text-slate-600">|</span>
                <span className="flex items-center gap-1">
                    <span className="text-slate-500">kanal</span>
                    <span className="font-mono text-slate-300">{ticket.channel}</span>
                </span>
                <span className="text-slate-600">|</span>
                <span className="flex items-center gap-1">
                    <span className={`h-1.5 w-1.5 rounded-full ${statusDotColor[ticket.status]}`} />
                    {statusLabel[ticket.status]}
                </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
                {ticket.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center rounded-full bg-slate-900/70 border border-slate-800 px-2 py-0.5 text-[10px] text-slate-400">
                        #{tag}
                    </span>
                ))}
            </div>
        </div>
    </div>
);

interface TicketDetailPanelProps {
    ticket: Ticket;
}

const TicketDetailPanel: React.FC<TicketDetailPanelProps> = ({ ticket }) => (
    <div className="h-full flex flex-col">
        <div className="border-b border-slate-800/70 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 bg-slate-950/60 backdrop-blur-xl">
            <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="font-mono text-[11px] text-slate-500">{ticket.id}</span>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${priorityColor[ticket.priority]}`}>
                        {priorityLabel[ticket.priority]}
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-slate-400">
                        <span className={`h-1.5 w-1.5 rounded-full ${statusDotColor[ticket.status]}`} />
                        {statusLabel[ticket.status]}
                    </span>
                </div>
                <h2 className="text-sm sm:text-base font-semibold tracking-tight">{ticket.title}</h2>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                    <span className="flex items-center gap-1">
                        <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-slate-800/80 text-[10px]">U</span>
                        {ticket.requester}
                    </span>
                    <span className="text-slate-600">|</span>
                    <span className="flex items-center gap-1">
                        kanal <span className="font-mono text-slate-300">{ticket.channel}</span>
                    </span>
                    <span className="text-slate-600">|</span>
                    <span>utworzono: <span className="text-slate-300">{formatDate(ticket.createdAt)}</span></span>
                </div>
            </div>

            <div className="flex flex-col items-end gap-2">
                <div className="flex gap-1.5">
                    <IconButton label="Odpowiedz" tone="primary">R</IconButton>
                    <IconButton label="Przypisz" tone="neutral">A</IconButton>
                    <IconButton label="Zamknij" tone="danger">X</IconButton>
                </div>
                <span className="text-[10px] text-slate-500">Ostatnia aktywnosc: {formatDate(ticket.lastActivity)}</span>
            </div>
        </div>

        <div className="flex-1 grid grid-rows-2 xl:grid-rows-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4 px-4 sm:px-6 py-4 overflow-auto">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/60 shadow-inner shadow-black/40 p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-300">Checklista</span>
                    <span className="text-[11px] text-slate-500">Ticket</span>
                </div>
                <div className="space-y-2 text-xs">
                    <ChecklistRow label="Zweryfikuj logi bota" done />
                    <ChecklistRow label="Kontakt z uzytkownikiem" done={ticket.status === "in_progress"} />
                    <ChecklistRow label="Eskalacja jesli priorytet wysoki" done={ticket.priority === "high" || ticket.priority === "urgent"} />
                    <ChecklistRow label="Test /ticket close na serwerze" done={false} />
                </div>
            </div>

            <div className="space-y-3 text-xs">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/60 shadow-inner shadow-black/40 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-400">Szczegoly</span>
                        <button className="text-[11px] text-sky-300 hover:text-sky-200">Edytuj</button>
                    </div>
                    <MetaRow label="Priorytet">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] ${priorityColor[ticket.priority]}`}>
                            {priorityLabel[ticket.priority]}
                        </span>
                    </MetaRow>
                    <MetaRow label="Status">{statusLabel[ticket.status]}</MetaRow>
                    <MetaRow label="Kanal">{ticket.channel}</MetaRow>
                    <MetaRow label="Tagi">
                        <div className="flex flex-wrap gap-1">
                            {ticket.tags.map((tag) => (
                                <span key={tag} className="inline-flex items-center rounded-full bg-slate-900/70 border border-slate-800 px-2 py-0.5 text-[10px] text-slate-400">
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </MetaRow>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/60 shadow-inner shadow-black/40 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] text-slate-400">Automacje & logi bota</span>
                        <span className="text-[11px] text-slate-500">ostatnie 24h</span>
                    </div>
                    <ActivityItem emoji="W" label="Webhook" detail="Event TICKET_CREATED na #support" ts="09:21" />
                    <ActivityItem emoji="M" label="Moderator" detail="Przejal ticket i zmienil status" ts="09:30" />
                    <ActivityItem emoji="R" label="Regula" detail="Priorytet podniesiony do Wysoki" ts="09:31" />
                </div>
            </div>
        </div>
    </div>
);

const Modules: React.FC = () => (
    <div className="col-span-2 rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Moduly bota</h2>
            <span className="text-[11px] text-slate-500">Automatyzacje</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {moduleCards.map((module) => (
                <div key={module.id} className="rounded-xl border border-slate-800 bg-slate-950/80 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-100">{module.name}</span>
                        <span
                            className={`text-[11px] px-2 py-0.5 rounded-full border ${module.status === "active"
                                    ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
                                    : "border-slate-600 text-slate-300 bg-slate-800"
                                }`}
                        >
                            {module.status === "active" ? "Aktywny" : "Wylaczony"}
                        </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">{module.description}</p>
                    <div className="text-[11px] text-slate-400">{module.scope}</div>
                    <div className="flex gap-2 text-[11px]">
                        <button className="px-2 py-1 rounded-lg border border-slate-700 text-slate-200 hover:border-sky-500/50 hover:text-sky-200 transition-colors">
                            Podglad
                        </button>
                        <button className="px-2 py-1 rounded-lg border border-slate-700 text-slate-200 hover:border-sky-500/50 hover:text-sky-200 transition-colors">
                            Edytuj
                        </button>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const CommandUsage: React.FC = () => (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Komendy</h2>
            <span className="text-[11px] text-slate-500">Statystyki</span>
        </div>
        <div className="space-y-2 text-xs">
            {commandStats.map((cmd) => (
                <div key={cmd.name} className="rounded-xl border border-slate-800 bg-slate-950/80 p-3 flex items-center justify-between gap-2">
                    <div>
                        <div className="font-medium text-slate-100">{cmd.name}</div>
                        <div className="text-slate-400">Sukces {cmd.success}% - Latencja {cmd.latency}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-lg font-semibold text-sky-200">{cmd.usage}</div>
                        <div className="text-[11px] text-slate-500">uzyc dzis</div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const Conversation: React.FC<{ requester: string; id: string }> = ({ requester, id }) => (
    <div className="col-span-2 rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Historia rozmowy</h2>
            <span className="text-[11px] text-slate-500">Live sync</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MessageBubble author="Bot" avatar="B" role="bot" timestamp="09:21">
                Hej <span className="font-medium">{requester}</span>! Ticket <span className="font-mono">{id}</span> zapisany. Moderacja odpowie wkrotce.
            </MessageBubble>
            <MessageBubble author="Raven#9001" avatar="R" role="user" timestamp="09:22">
                Po ostatnim update komenda <code className="px-1 rounded bg-slate-900 border border-slate-800">/ticket close</code> rzuca blad.
            </MessageBubble>
            <MessageBubble author="Moderator" avatar="M" role="mod" timestamp="09:30">
                Sprawdzam logi bota dla Twojego serwera. Prosze oznacz @Support i ponownie wyslij komende.
            </MessageBubble>
            <MessageBubble author="Bot" avatar="B" role="bot" timestamp="09:31">
                Ticket ustawiony na priorytet wysoki. Eskalacja do kolejki live.
            </MessageBubble>
        </div>
    </div>
);

const EventStream: React.FC = () => (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/70 shadow-inner shadow-black/40 p-4 space-y-3">
        <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Eventy i logi</h2>
            <span className="text-[11px] text-slate-500">24h</span>
        </div>
        <div className="space-y-2 text-xs">
            {eventLog.map((event) => (
                <div key={event.id} className="flex items-start gap-2 rounded-xl border border-slate-800 bg-slate-950/80 p-3">
                    <div
                        className={`h-7 w-7 rounded-full border flex items-center justify-center text-[11px] font-semibold ${event.type === "info"
                                ? "border-sky-500/40 text-sky-200 bg-sky-500/10"
                                : event.type === "warn"
                                    ? "border-amber-500/40 text-amber-200 bg-amber-500/10"
                                    : "border-rose-500/40 text-rose-200 bg-rose-500/10"
                            }`}
                    >
                        {event.type === "info" ? "I" : event.type === "warn" ? "!" : "X"}
                    </div>
                    <div className="flex-1">
                        <div className="flex justify-between text-[11px]">
                            <span className="font-medium text-slate-200">{event.detail}</span>
                            <span className="text-slate-500">{event.time}</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const SidebarItem: React.FC<{ label: string; active?: boolean }> = ({ label, active = false }) => (
    <button
        className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors ${active ? "bg-slate-900 text-slate-50 border border-slate-700 shadow-sm shadow-sky-500/30" : "text-slate-400 hover:text-slate-100 hover:bg-slate-900/60"
            }`}
    >
        <span className="text-sm">-</span>
        <span>{label}</span>
    </button>
);

const StatsCard: React.FC<{ label: string; value: number; trend: string; tone: "primary" | "success" | "alert" | "neutral" }> = ({
    label,
    value,
    trend,
    tone,
}) => {
    const toneConfig: Record<
        "primary" | "success" | "alert" | "neutral",
        { border: string; text: string; pillBg: string; pillText: string }
    > = {
        primary: { border: "border-sky-500/40", text: "text-sky-100", pillBg: "bg-sky-500/15", pillText: "text-sky-300" },
        success: { border: "border-emerald-500/40", text: "text-emerald-100", pillBg: "bg-emerald-500/15", pillText: "text-emerald-300" },
        alert: { border: "border-rose-500/40", text: "text-rose-100", pillBg: "bg-rose-500/15", pillText: "text-rose-300" },
        neutral: { border: "border-slate-700", text: "text-slate-100", pillBg: "bg-slate-700/80", pillText: "text-slate-200" },
    };

    const cfg = toneConfig[tone];

    return (
        <div className={`rounded-2xl border bg-slate-950/60 px-3.5 py-2.5 shadow-inner shadow-black/40 ${cfg.border}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-400">{label}</span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${cfg.pillBg} ${cfg.pillText}`}>{trend}</span>
            </div>
            <div className={`mt-1 text-lg font-semibold ${cfg.text}`}>{value}</div>
        </div>
    );
};

const SelectChip: React.FC<{
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
    <div className="relative">
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="appearance-none text-xs rounded-xl bg-slate-900/70 border border-slate-800 px-3 py-2 text-slate-100 pr-8 focus:outline-none focus:ring-2 focus:ring-sky-500/70 focus:border-sky-500/70"
        >
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[10px] text-slate-500">{label}</span>
    </div>
);

const MessageBubble: React.FC<{ author: string; avatar: string; role: "user" | "mod" | "bot"; timestamp: string; children: React.ReactNode }> = ({
    author,
    avatar,
    role,
    timestamp,
    children,
}) => {
    const isMod = role === "mod";
    const isBot = role === "bot";

    return (
        <div className="flex gap-2 rounded-xl border border-slate-800 bg-slate-950/80 p-3">
            <div className="mt-0.5 h-8 w-8 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-sm font-semibold">
                {avatar}
            </div>
            <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[11px]">
                        <span className="font-medium text-slate-200">{author}</span>
                        {isMod && (
                            <span className="rounded-full bg-emerald-500/10 border border-emerald-500/40 px-1.5 py-0.5 text-[10px] text-emerald-300">
                                Mod
                            </span>
                        )}
                        {isBot && (
                            <span className="rounded-full bg-sky-500/10 border border-sky-500/40 px-1.5 py-0.5 text-[10px] text-sky-300">
                                Bot
                            </span>
                        )}
                    </div>
                    <span className="text-[10px] text-slate-500">{timestamp}</span>
                </div>
                <div
                    className={`inline-block max-w-full rounded-2xl px-3 py-2 text-[11px] leading-relaxed ${isBot
                            ? "bg-sky-950/70 border border-sky-800/70 text-sky-50"
                            : isMod
                                ? "bg-slate-900/90 border border-emerald-800/60 text-slate-50"
                                : "bg-slate-900/80 border border-slate-800 text-slate-100"
                        }`}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

const ActivityItem: React.FC<{ emoji: string; label: string; detail: string; ts: string }> = ({
    emoji,
    label,
    detail,
    ts,
}) => (
    <div className="flex items-start gap-2">
        <div className="mt-0.5 h-6 w-6 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-xs">
            {emoji}
        </div>
        <div className="flex-1">
            <div className="flex justify-between text-[11px]">
                <span className="font-medium text-slate-200">{label}</span>
                <span className="text-slate-500">{ts}</span>
            </div>
            <p className="text-[11px] text-slate-400">{detail}</p>
        </div>
    </div>
);

const QuickAction: React.FC<{ label: string; detail: string }> = ({ label, detail }) => (
    <button className="w-full text-left rounded-xl border border-slate-800 bg-slate-950/80 px-3 py-2 hover:border-sky-500/50 hover:text-sky-200 transition-colors">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[11px] text-slate-400">{detail}</div>
    </button>
);

const ControlCard: React.FC<{ title: string; subtitle: string; state: "ON" | "OFF"; tone: "success" | "neutral" | "alert" }> = ({
    title,
    subtitle,
    state,
    tone,
}) => {
    const toneClass: Record<"success" | "neutral" | "alert", string> = {
        success: "border-emerald-500/40 text-emerald-200",
        neutral: "border-slate-700 text-slate-200",
        alert: "border-amber-500/40 text-amber-200",
    };

    return (
        <div className={`rounded-xl border bg-slate-950/80 p-3 flex items-center justify-between ${toneClass[tone]}`}>
            <div>
                <div className="text-sm font-semibold">{title}</div>
                <div className="text-[11px] text-slate-400">{subtitle}</div>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded-lg border border-slate-700 bg-slate-900">{state}</span>
        </div>
    );
};

const ChecklistRow: React.FC<{ label: string; done?: boolean }> = ({ label, done = false }) => (
    <div className="flex items-center gap-2 text-slate-200">
        <span className={`h-4 w-4 rounded border ${done ? "border-emerald-500 bg-emerald-500/80" : "border-slate-600 bg-slate-900"}`} />
        <span className="text-[12px] text-slate-300">{label}</span>
    </div>
);

const IconButton: React.FC<{ children: React.ReactNode; label: string; tone?: "primary" | "neutral" | "danger" }> = ({
    children,
    label,
    tone = "neutral",
}) => {
    const toneClass: Record<"primary" | "neutral" | "danger", string> = {
        primary: "bg-sky-500/10 text-sky-200 border-sky-500/50 hover:bg-sky-500/20",
        neutral: "bg-slate-900/80 text-slate-200 border-slate-700 hover:border-sky-500/40",
        danger: "bg-rose-500/5 text-rose-200 border-rose-500/40 hover:bg-rose-500/15",
    };

    return (
        <button className={`inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[11px] font-medium transition-colors ${toneClass[tone]}`}>
            <span className="text-sm">{children}</span>
            <span>{label}</span>
        </button>
    );
};

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex items-start justify-between gap-3">
        <span className="text-[11px] text-slate-500">{label}</span>
        <div className="text-[11px] text-slate-200 text-right">{children}</div>
    </div>
);

const formatDate = (iso: string): string => {
    const date = new Date(iso);
    return date.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const formatTime = (iso: string): string => {
    const date = new Date(iso);
    return date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
};

export default Test;
